import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  formatHermesChoicesReply,
  hermesChoiceSelectionComplete,
  hermesChoicesReadyToSubmit,
  HERMES_KB_ENABLE_GUIDE,
  type HermesChoiceCard as ChoiceData,
} from "@/lib/hermes-choices";

type Props = {
  choices: ChoiceData[];
  disabled?: boolean;
  onSubmit: (message: string) => void | Promise<void>;
};

/** 同轮一张或多张澄清卡：先逐项选完，再统一提交一条用户消息。 */
export function HermesChoiceCards({ choices, disabled = false, onSubmit }: Props) {
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const locked = disabled || submitted || submitting;
  const ready = hermesChoicesReadyToSubmit(choices, selections);
  const showKbGuide = choices.some(
    (choice) => choice.kind === "kb_enable" || choice.kind === "capability_upgrade",
  );

  const hint = useMemo(() => {
    if (submitted) return "已提交";
    if (choices.length > 1) return "请逐项选完后，点下方一次确认";
    return choices[0]?.allow_multiple ? "可多选" : "请选一项";
  }, [choices, submitted]);

  function toggle(choice: ChoiceData, optionId: string) {
    if (locked) return;
    setSelections((current) => {
      const selected = current[choice.id] ?? [];
      const next = choice.allow_multiple
        ? selected.includes(optionId)
          ? selected.filter((id) => id !== optionId)
          : [...selected, optionId]
        : selected.includes(optionId)
          ? []
          : [optionId];
      return { ...current, [choice.id]: next };
    });
  }

  async function handleSubmit() {
    if (!ready || locked) return;
    setSubmitting(true);
    try {
      await onSubmit(formatHermesChoicesReply(choices, selections));
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (choices.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        请选择
        {choices.length > 1 ? `（${choices.length} 项）` : ""}
        {hint ? ` · ${hint}` : ""}
      </p>
      {choices.map((choice) => {
        const selected = selections[choice.id] ?? [];
        const complete = hermesChoiceSelectionComplete(choice, selected);
        return (
          <div
            key={choice.id}
            className="rounded-lg border border-border bg-card px-3 py-3 text-sm shadow-sm"
          >
            <p className="font-medium text-foreground">{choice.prompt}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {choice.allow_multiple ? "可多选" : "请选一项"}
              {complete ? " · 已选" : ""}
            </p>
            <ul className="mt-3 space-y-2">
              {choice.options.map((opt) => {
                const checked = selected.includes(opt.id);
                return (
                  <li key={opt.id}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      disabled={locked}
                      onClick={() => toggle(choice, opt.id)}
                      className={cn(
                        "flex w-full items-start gap-2 rounded-md border px-2 py-1.5 text-left transition-colors",
                        checked
                          ? "border-primary/40 bg-primary/5"
                          : "border-transparent hover:bg-muted/60",
                        locked && "pointer-events-none opacity-70",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border",
                          checked
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-muted-foreground/40",
                        )}
                      >
                        {checked ? <Check className="size-3" /> : null}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium">{opt.label}</span>
                        {opt.hint ? (
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            {opt.hint}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {showKbGuide ? (
        <pre className="whitespace-pre-wrap rounded-md bg-muted/50 px-2 py-2 text-[11px] leading-relaxed text-muted-foreground">
          {HERMES_KB_ENABLE_GUIDE}
        </pre>
      ) : null}
      <div className="flex justify-end">
        <Button type="button" size="sm" disabled={!ready || locked} onClick={() => void handleSubmit()}>
          {submitting
            ? "提交中…"
            : submitted
              ? "已确认"
              : choices.length > 1
                ? "确认全部选择"
                : "确认选择"}
        </Button>
      </div>
    </div>
  );
}

/** @deprecated 请用 HermesChoiceCards；保留别名以免旧 import 断裂 */
export function HermesChoiceCard({
  choice,
  disabled,
  onSubmit,
}: {
  choice: ChoiceData;
  disabled?: boolean;
  onSubmit: (message: string) => void | Promise<void>;
}) {
  return <HermesChoiceCards choices={[choice]} disabled={disabled} onSubmit={onSubmit} />;
}
